// /**
//  * Products - list
//  */

// React
import React, { useContext } from 'react';
import Can from '../../../components/Can';
// UI
import theme from '../../../theme';
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiPencilOutline,
  mdiChevronDown,
  mdiChevronUp,
  mdiFormatListCheckbox,
  mdiConsolidate,
} from '@mdi/js';

// Type
import { Product } from '../interfaces';
import { Link, useHistory } from 'react-router-dom';
import AuthContext from '../../../authContext';
import stableSort from '../../../utils/Table/stableSort';
import getComparator from '../../../utils/Table/getComparator';

type Data = Product;
type Props = {
  data: any;
  handleOpenConfirmDisable: (id: number) => void;
  handleOpen: (id: number, type: string) => void;
  changeStatus: any;
};

type Ord = 'asc' | 'desc';

interface HeadCell {
  id: keyof Data;
  label: string;
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      textAlign: 'left',
      backgroundColor: '#D5DADD !important',
      color: '#212121',
    },
    body: {
      textAlign: 'left',
      fontSize: 14,
      paddingLeft: '25px',
    },
  })
)(TableCell);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
    },
    table: {
      minWidth: 750,
    },
    columnText: {
      padding: '0 10px',
    },
    dot: {
      position: 'relative',
      display: 'inline-block',
      top: '0',
      left: '-8px',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    hover: {
      '&:hover': {
        backgroundColor: '#F8F9FA !important',
      },
    },
  })
);

const IconSort = (order: Ord) => {
  return (
    <Icon path={order === 'asc' ? mdiChevronDown : mdiChevronUp} size={1} />
  );
};

function EnhancedTableHead(props: EnhancedTableProps) {
  const authContext = useContext(AuthContext);
  const { order, orderBy, onRequestSort } = props;
  const classes = useStyles();
  const createSortHandler = (property: keyof Data) => (
    event: React.MouseEvent<unknown>
  ) => {
    onRequestSort(event, property);
  };

  let headCells: HeadCell[] = [
    {
      id: 'name',
      label: 'نام کالا',
    },
    {
      id: 'code',
      label: 'کد کالا',
    },
    {
      id: 'id',
      label: 'شناسه کالا',
    },
    {
      id: 'categoryName',
      label: 'دسته بندی',
    },
    {
      id: 'sumCount',
      label: 'موجودی',
    },
  ];

  return (
    <TableHead>
      <TableRow>
        <StyledTableCell />
        {headCells.map(headCell => {
          let orderStatus = orderBy === headCell.id ? order : 'asc';
          return (
            <StyledTableCell
              key={headCell.id}
              align="center"
              sortDirection={orderBy === headCell.id ? order : false}
              data-cy={headCell.id}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderStatus}
                onClick={createSortHandler(headCell.id)}
                IconComponent={() => IconSort(orderStatus)}
              >
                <span className={classes.columnText}>{headCell.label}</span>
              </TableSortLabel>
            </StyledTableCell>
          );
        })}
        <StyledTableCell />
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const authContext = useContext(AuthContext);
  const classes = useStyles();
  const history = useHistory();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('id');
  const [dense] = React.useState(false);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <TableContainer>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
          aria-label="enhanced table"
        >
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={props.data.length}
          />
          <TableBody>
            {stableSort(props.data, getComparator(order, orderBy))
              .slice()
              .map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow className={classes.hover} key={index}>
                    <StyledTableCell align="center">
                      {' '}
                      <span
                        className={classes.dot}
                        style={{
                          backgroundColor:
                            row.status === 'SUSPENDED' ||
                              row.status === 'REGISTERED'
                              ? theme.palette.error.main
                              : theme.palette.green.main,
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell
                      data-cy="name"
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      {row.name}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      data-cy="code"
                      scope="row"
                      align="center"
                    >
                      {row.code}
                    </StyledTableCell>
                    <StyledTableCell data-cy="id" align="center">
                      {row.id}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      data-cy="categoryName"
                      scope="row"
                      align="center"
                    >
                      {row.categoryName}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      data-cy="sumCount"
                      scope="row"
                      align="center"
                    >
                      {row.sumCount}
                    </StyledTableCell>
                    <StyledTableCell style={{ textAlign: 'center' }}>
                      <>
                        <Can
                          perform="product:edit"
                          role={authContext.user.role}
                          yes={() => (
                            <>
                              <Link
                                to={`/edit-product/${row.id}`}
                                data-cy="edit"
                              >
                                <IconButton title="ویرایش کالا" size="small">
                                  <Icon
                                    color={theme.palette.blue.main}
                                    path={mdiPencilOutline}
                                    size={1}
                                    data-cy="edit"
                                  />
                                </IconButton>
                              </Link>
                              <IconButton
                                title="جزئیات و تنوع کالا"
                                size="small"
                                onClick={() =>
                                  history.push(`/your-products/${row.id}`)
                                }
                                data-cy="detail"
                              >
                                <Icon path={mdiFormatListCheckbox} size={1} />
                              </IconButton>
                            </>
                          )}
                          no={() => (
                            <Link to={`/seller-products/${row.id}`}>
                              <IconButton
                                title="جزئیات و تنوع کالا"
                                size="small"
                              >
                                <Icon path={mdiFormatListCheckbox} size={1} />
                              </IconButton>
                            </Link>
                          )}
                        />
                        <IconButton
                          title="تاریخچه ورود و خروج"
                          size="small"
                          onClick={() => props.handleReport(row.id)}
                          data-cy="history"
                        >
                          <Icon
                            path={mdiConsolidate}
                            size={1}
                            color={theme.palette.blue.main}
                          />
                        </IconButton>
                      </>
                    </StyledTableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
